import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { getCurrentUser, updateActiveStatus } from 'services/users'

// Heartbeat: invia un active_check al backend ogni 5 minuti
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000

// Interfaccia per i dati utente dal backend
export interface CurrentUser {
  id?: number
  first_name?: string
  last_name?: string
  email?: string
  username?: string
  role?: string
  is_superuser?: boolean
  working_hours?: string
  profile_photo_url?: string | null
  language?: string
  enabled_modules?: Record<string, boolean>
}

interface UserContextType {
  user: CurrentUser | null
  loading: boolean
  refreshUser: () => Promise<void>
  setUser: (user: CurrentUser | null) => void
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchUser = useCallback(async () => {
    // Skip if no token
    if (!localStorage.getItem('accessToken')) {
      setUser(null)
      setLoading(false)
      return
    }

    try {
      const response = await getCurrentUser()
      setUser(response.data)
    } catch (error) {
      console.error('[UserContext] Failed to fetch user:', error)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch user on mount
  useEffect(() => {
    fetchUser()
  }, [fetchUser])

  // Heartbeat globale: ping il backend ogni 5 minuti finché c'è un utente autenticato.
  // Se user diventa null (logout, sessione scaduta) l'effect si re-runna,
  // l'interval precedente viene pulito e il nuovo run esce subito.
  useEffect(() => {
    if (!user?.id) return

    let cancelled = false

    const ping = () => {
      if (cancelled) return
      updateActiveStatus(true).catch(err => console.warn('heartbeat failed', err))
    }

    ping()
    const interval = setInterval(ping, HEARTBEAT_INTERVAL_MS)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [user?.id])

  // refreshUser: permette ai componenti di ricaricare i dati utente dal backend
  const refreshUser = useCallback(async () => {
    if (!localStorage.getItem('accessToken')) {
      setUser(null)
      return
    }

    try {
      const response = await getCurrentUser()
      setUser(response.data)
      console.log('[UserContext] User refreshed:', response.data)
    } catch (error) {
      console.error('[UserContext] Failed to refresh user:', error)
    }
  }, [])

  // updateUser: aggiorna i dati utente direttamente senza chiamata API
  // Utile quando si hanno già i dati aggiornati dalla response di una PATCH
  const updateUser = useCallback((userData: CurrentUser | null) => {
    setUser(userData)
  }, [])

  return (
    <UserContext.Provider value={{ user, loading, refreshUser, setUser: updateUser }}>
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}
